import "./App.css";
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Start from "./components/Start";
import Timer from "./components/Timer";
import KbcQuiz from "./components/KbcQuiz";
import Scoreboard from "./components/Scoreboard";
import { questionsData } from "./data/questions";

function App() {
  const [username, setUsername] = useState(null);
  const [timeOut, setTimeOut] = useState(false);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [earned, setEarned] = useState("$ 0");
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  const moneyPyramid = useMemo(
    () =>
      [
        { id: 1, amount: "$ 100" },
        { id: 2, amount: "$ 200" },
        { id: 3, amount: "$ 300" },
        { id: 4, amount: "$ 500" },
        { id: 5, amount: "$ 1.000" },
        { id: 6, amount: "$ 2.000" },
        { id: 7, amount: "$ 4.000" },
        { id: 8, amount: "$ 8.000" },
        { id: 9, amount: "$ 16.000" },
        { id: 10, amount: "$ 32.000" },
        { id: 11, amount: "$ 64.000" },
        { id: 12, amount: "$ 125.000" },
        { id: 13, amount: "$ 250.000" },
        { id: 14, amount: "$ 500.000" },
        { id: 15, amount: "$ 1.000.000" },
      ].reverse(),
    []
  );

  useEffect(() => {
    if (questionNumber > 1) {
      const earnedAmount = moneyPyramid.find((m) => m.id === questionNumber - 1)?.amount || "$ 0";
      setEarned(earnedAmount);
      
      // Convert earned amount to number for scoreboard
      const numericScore = parseInt(earnedAmount.replace(/[^0-9]/g, '')) || 0;
      setFinalScore(numericScore);
    }
  }, [questionNumber, moneyPyramid]);

  useEffect(() => {
    if (timeOut || questionNumber > questionsData.length) {
      const timer = setTimeout(() => {
        setShowScoreboard(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [timeOut, questionNumber]);

  // Reset selectedAnswer when question changes
  useEffect(() => {
    setSelectedAnswer(null);
  }, [questionNumber]);

  const handlePlayAgain = () => {
    setQuestionNumber(1);
    setTimeOut(false);
    setEarned("$ 0");
    setFinalScore(0);
    setShowScoreboard(false);
    setSelectedAnswer(null);
  };

  const handleNewPlayer = () => {
    setUsername(null);
    setQuestionNumber(1);
    setTimeOut(false);
    setEarned("$ 0");
    setFinalScore(0);
    setShowScoreboard(false);
    setSelectedAnswer(null);
  };

  return (
    <div className="app">
      <AnimatePresence mode="wait">
        {!username ? (
          <motion.div
            key="start"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5 }}
          >
            <Start setUsername={setUsername} />
          </motion.div>
        ) : showScoreboard ? (
          <motion.div
            key="scoreboard"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
          >
            <Scoreboard 
              currentScore={finalScore}
              playerName={username}
              onPlayAgain={handlePlayAgain}
              onBackToStart={handleNewPlayer}
            />
          </motion.div>
        ) : (
          <motion.div
            key="game"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="game-container"
          >
            <div className="main">
              {timeOut || questionNumber > questionsData.length ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.8 }}
                  className="end-screen"
                >
                  <h1 className="endText">
                    {questionNumber > questionsData.length ? "🎉 Congratulations! 🎉" : "Game Over!"}
                  </h1>
                  <h2 className="earnedText">You earned: {earned}</h2>
                  <p className="loading-text">Loading scoreboard...</p>
                </motion.div>
              ) : (
                <>
                  <div className="top">
                    <div className="timer">
                      <Timer
                        setTimeOut={setTimeOut}
                        questionNumber={questionNumber}
                        isAnswerSelected={selectedAnswer !== null}
                      />
                    </div>
                  </div>
                  <div className="bottom">
                    <KbcQuiz
                      data={questionsData}
                      questionNumber={questionNumber}
                      setQuestionNumber={setQuestionNumber}
                      setTimeOut={setTimeOut}
                      selectedAnswer={selectedAnswer}
                      setSelectedAnswer={setSelectedAnswer}
                    />
                  </div>
                </>
              )}
            </div>
            <motion.div 
              className="pyramid"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <ul className="moneyList">
                {moneyPyramid.map((m) => (
                  <motion.li
                    key={m.id}
                    className={
                      questionNumber === m.id
                        ? "moneyListItem active"
                        : "moneyListItem"
                    }
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <span className="moneyListItemNumber">{m.id}</span>
                    <span className="moneyListItemAmount">{m.amount}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
